<template>
  <div>
    <!-- KPIs -->
    <v-row class="mb-2">
      <v-col cols="12" sm="6" md="3" v-for="kpi in kpis" :key="kpi.label">
        <v-card outlined class="rounded-lg pa-4">
          <div class="text-caption grey--text text--darken-1 font-weight-medium">{{ kpi.label }}</div>
          <div class="text-h5 font-weight-bold mt-1" style="color:#1b273e;">{{ kpi.value }}</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters + actions -->
    <v-card outlined class="rounded-lg pa-4 mb-4">
      <v-row dense align="center">
        <v-col cols="12" sm="5">
          <v-text-field
            v-model="search"
            placeholder="Buscar por nombre o email"
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            hide-details
            clearable
            @input="onSearchChange"
          />
        </v-col>
        <v-col cols="6" sm="3">
          <v-select
            v-model="statusFilter"
            :items="statusOptions"
            label="Estado"
            outlined
            dense
            hide-details
            clearable
            @change="reload"
          />
        </v-col>
        <v-col cols="12" sm="4" class="text-sm-right">
          <v-btn
            outlined
            class="rounded-lg text-none mr-2"
            @click="downloadCsv"
          >
            <v-icon left small>mdi-download</v-icon> CSV
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Table -->
    <v-card outlined class="rounded-lg">
      <v-data-table
        :headers="headers"
        :items="orders"
        :loading="loading"
        :server-items-length="totalDocs"
        :options.sync="tableOptions"
        :footer-props="{ itemsPerPageOptions: [10, 20, 50] }"
        no-data-text="Sin órdenes para mostrar"
        loading-text="Cargando órdenes..."
      >
        <template #[`item.folio`]="{ item }">
          <span class="font-weight-bold">{{ item.folio || item._id.slice(-6).toUpperCase() }}</span>
        </template>
        <template #[`item.buyer`]="{ item }">
          <div class="d-flex flex-column">
            <span class="font-weight-medium">{{ buyerName(item) }}</span>
            <span class="text-caption grey--text text--darken-1">{{ buyerEmail(item) }}</span>
          </div>
        </template>
        <template #[`item.createdAt`]="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>
        <template #[`item.total`]="{ item }">
          {{ formatCurrency(item.total, item.currency) }}
        </template>
        <template #[`item.paymentMethod`]="{ item }">
          {{ paymentMethodLabel(item.paymentInformation && item.paymentInformation.paymentMethod) }}
        </template>
        <template #[`item.status`]="{ item }">
          <v-chip :color="statusColor(item.status)" text-color="white" small class="font-weight-bold">
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>
        <template #[`item.invoice`]="{ item }">
          <v-icon v-if="item.invoice" color="success">mdi-file-check-outline</v-icon>
          <span v-else class="text-caption grey--text">—</span>
        </template>
        <template #[`item.paymentPlan`]="{ item }">
          <v-chip v-if="item.paymentPlan" small outlined color="primary">
            {{ item.paymentPlan.paidInstallments || 0 }}/{{ item.paymentPlan.installmentsCount || 0 }}
          </v-chip>
          <span v-else class="text-caption grey--text">—</span>
        </template>
        <template #[`item.actions`]="{ item }">
          <v-menu offset-y left>
            <template #activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="actionInvoice(item)">
                <v-list-item-icon><v-icon small>mdi-file-document-outline</v-icon></v-list-item-icon>
                <v-list-item-title>Facturar</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="item.status !== 'COMPLETED'" @click="actionMarkPaid(item)">
                <v-list-item-icon><v-icon small>mdi-cash-check</v-icon></v-list-item-icon>
                <v-list-item-title>Marcar como pagada</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="item.status === 'COMPLETED'" @click="actionMarkDelivered(item)">
                <v-list-item-icon><v-icon small color="#1b273e">mdi-package-variant-closed-check</v-icon></v-list-item-icon>
                <v-list-item-title>Marcar como entregado</v-list-item-title>
              </v-list-item>
              <v-list-item @click="actionAssignPlan(item)">
                <v-list-item-icon><v-icon small>mdi-calendar-clock</v-icon></v-list-item-icon>
                <v-list-item-title>Asignar plan de pagos</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="item.invoice" @click="actionPaymentComplement(item)">
                <v-list-item-icon><v-icon small>mdi-file-plus-outline</v-icon></v-list-item-icon>
                <v-list-item-title>Complemento de pago</v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item @click="actionCancel(item)">
                <v-list-item-icon><v-icon small color="error">mdi-cancel</v-icon></v-list-item-icon>
                <v-list-item-title class="error--text">Cancelar</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="item.invoice" @click="actionCreditNote(item)">
                <v-list-item-icon><v-icon small color="error">mdi-file-remove-outline</v-icon></v-list-item-icon>
                <v-list-item-title class="error--text">Nota de crédito</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { mapActions, mapMutations } from "vuex";

export default {
  name: "ProductServiceSalesTab",
  props: {
    productId: { type: String, required: true },
  },
  data() {
    return {
      loading: false,
      orders: [],
      totalDocs: 0,
      search: "",
      searchTimer: null,
      statusFilter: null,
      statusOptions: [
        { text: "Todos", value: null },
        { text: "Pendientes", value: "PENDING" },
        { text: "Completadas", value: "COMPLETED" },
        { text: "Entregadas", value: "DELIVERED" },
        { text: "Canceladas", value: "CANCELLED" },
      ],
      tableOptions: { page: 1, itemsPerPage: 10 },
      headers: [
        { text: "Folio", value: "folio", sortable: false },
        { text: "Comprador", value: "buyer", sortable: false },
        { text: "Fecha", value: "createdAt" },
        { text: "Monto", value: "total" },
        { text: "Método de pago", value: "paymentMethod", sortable: false },
        { text: "Estado", value: "status" },
        { text: "Factura", value: "invoice", sortable: false, align: "center" },
        { text: "Plan pagos", value: "paymentPlan", sortable: false, align: "center" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
    };
  },
  watch: {
    tableOptions: {
      deep: true,
      handler() {
        this.reload();
      },
    },
    productId() {
      this.reload();
    },
  },
  mounted() {
    this.reload();
  },
  computed: {
    kpis() {
      const completed = this.orders.filter(o => o.status === "COMPLETED");
      const pending = this.orders.filter(o => o.status === "PENDING");
      const totalSales = completed.reduce((sum, o) => sum + (o.total || 0), 0);
      const invoiced = this.orders.filter(o => o.invoice).reduce((s, o) => s + (o.total || 0), 0);
      return [
        { label: "Ventas totales", value: this.formatCurrency(totalSales, "MXN") },
        { label: "Órdenes completadas", value: completed.length },
        { label: "Órdenes pendientes", value: pending.length },
        { label: "Monto facturado", value: this.formatCurrency(invoiced, "MXN") },
      ];
    },
  },
  methods: {
    ...mapActions("productServices", ["fetchProductServiceOrders", "markOrderAsDelivered"]),
    ...mapMutations("notification", ["show"]),
    async reload() {
      this.loading = true;
      const params = {
        page: this.tableOptions.page,
        limit: this.tableOptions.itemsPerPage,
      };
      if (this.search) params.name = this.search;
      const res = await this.fetchProductServiceOrders({ id: this.productId, params });
      this.loading = false;
      if (res.status) {
        const docs = res.data.orders || [];
        this.orders = this.statusFilter ? docs.filter(o => o.status === this.statusFilter) : docs;
        this.totalDocs = res.data.totalDocs || this.orders.length;
      }
    },
    onSearchChange() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.reload(), 350);
    },
    buyerName(o) {
      if (o.member) {
        return `${o.member.name || ""} ${o.member.firstLastname || ""}`.trim() || "—";
      }
      return (o.paymentInformation && o.paymentInformation.name) || "—";
    },
    buyerEmail(o) {
      return (o.member && o.member.email) || (o.paymentInformation && o.paymentInformation.email) || "";
    },
    formatDate(d) {
      if (!d) return "—";
      return new Date(d).toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "numeric" });
    },
    formatCurrency(v, c) {
      const value = Number(v || 0);
      return new Intl.NumberFormat("es-MX", { style: "currency", currency: c || "MXN" }).format(value);
    },
    paymentMethodLabel(m) {
      return ({
        CREDIT_CARD: "Tarjeta",
        CASH: "Efectivo",
        BANK_TRANSFER: "Transferencia",
        PEIBO: "Peibo",
        MANUALLY_COMPLETED: "Manual",
      })[m] || m || "—";
    },
    statusLabel(s) {
      return ({
        PENDING: "Pendiente",
        COMPLETED: "Completada",
        DELIVERED: "Entregada",
        CANCELLED: "Cancelada",
        IN_PROGRESS: "En progreso",
      })[s] || s;
    },
    statusColor(s) {
      return ({
        PENDING: "warning",
        COMPLETED: "success",
        DELIVERED: "#1b273e",
        CANCELLED: "error",
        IN_PROGRESS: "grey",
      })[s] || "grey";
    },
    downloadCsv() {
      if (!this.orders.length) {
        this.show({ text: "No hay órdenes para exportar.", color: "warning" });
        return;
      }
      const rows = [
        ["Folio", "Comprador", "Email", "Fecha", "Monto", "Moneda", "Método", "Estado"],
        ...this.orders.map(o => [
          o.folio || o._id,
          this.buyerName(o),
          this.buyerEmail(o),
          this.formatDate(o.createdAt),
          o.total || 0,
          o.currency || "MXN",
          this.paymentMethodLabel(o.paymentInformation && o.paymentInformation.paymentMethod),
          this.statusLabel(o.status),
        ]),
      ];
      const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `ordenes-producto-${this.productId}.csv`;
      a.click();
    },
    openCreateOrderDialog() {
      this.show({ text: "Función disponible próximamente.", color: "info" });
    },
    actionInvoice(item) {
      this.$emit("action", { type: "invoice", order: item });
      this.show({ text: "Facturación pendiente de integración.", color: "info" });
    },
    actionMarkPaid(item) {
      this.$emit("action", { type: "markPaid", order: item });
    },
    actionAssignPlan(item) {
      this.$emit("action", { type: "assignPlan", order: item });
    },
    actionPaymentComplement(item) {
      this.$emit("action", { type: "paymentComplement", order: item });
    },
    actionCancel(item) {
      this.$emit("action", { type: "cancel", order: item });
    },
    async actionMarkDelivered(item) {
      const res = await this.markOrderAsDelivered(item._id);
      if (res.status) {
        item.status = "DELIVERED";
        this.show({ text: "Orden marcada como entregada.", color: "success" });
      } else {
        this.show({ text: res.message || "No se pudo actualizar el estado.", color: "error" });
      }
    },
    actionCreditNote(item) {
      this.$emit("action", { type: "creditNote", order: item });
    },
  },
};
</script>

<style scoped>
</style>
